import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { SectionHeading } from "../Reveal";
import { useLead } from "../../context/LeadContext";

const EMPTY = {
  firma: "", vorname: "", nachname: "", plz_ort: "", email: "", telefon: "",
  anzahl_fahrzeuge: "", modell: "", kauf_leasing: "", eintausch: "", bemerkungen: "", datenschutz: false,
};

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-950 focus:ring-1 focus:ring-black transition-colors";

export default function LeadForm({ models }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const { selectedModel, setSelectedModel } = useLead();

  useEffect(() => {
    if (selectedModel) {
      setForm((f) => ({ ...f, modell: selectedModel }));
      setSelectedModel("");
    }
  }, [selectedModel, setSelectedModel]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.datenschutz) {
      setError("Bitte akzeptieren Sie die Datenschutzerklärung.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", form);
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="offerte" data-testid="leadform-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHeading
              number="05"
              label="Offerte"
              title="Jetzt persönliche Firmenofferte anfordern"
              sub="Teilen Sie uns kurz mit, welches Fahrzeug Sie interessiert. Unser Verkaufsteam meldet sich persönlich bei Ihnen."
            />
            <div className="space-y-4 text-sm text-neutral-600 -mt-8">
              <p className="flex items-start gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-neutral-950" /> Kostenlos und unverbindlich</p>
              <p className="flex items-start gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-neutral-950" /> Individuell auf Ihr Unternehmen abgestimmt</p>
              <p className="flex items-start gap-3"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-neutral-950" /> Persönliche Betreuung durch unser KIA Verkaufsteam</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div data-testid="leadform-success" className="border border-neutral-200 bg-neutral-50 p-10 sm:p-14 text-center h-full flex flex-col items-center justify-center">
                <CheckCircle2 size={44} strokeWidth={1.25} className="text-neutral-950" />
                <h3 className="mt-6 text-2xl font-medium tracking-tight text-neutral-950">Vielen Dank für Ihre Anfrage.</h3>
                <p className="mt-3 text-neutral-600 text-sm max-w-sm leading-relaxed">
                  Unser KIA Verkaufsteam wird sich persönlich bei Ihnen melden.
                </p>
              </div>
            ) : (
              <form data-testid="leadform" onSubmit={submit} className="border border-neutral-200 p-7 sm:p-10 space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Firma *</label>
                  <input data-testid="leadform-firma" required className={inputCls} value={form.firma} onChange={(e) => set("firma", e.target.value)} placeholder="Muster AG" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Vorname *</label>
                    <input data-testid="leadform-vorname" required className={inputCls} value={form.vorname} onChange={(e) => set("vorname", e.target.value)} placeholder="Max" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Nachname *</label>
                    <input data-testid="leadform-nachname" required className={inputCls} value={form.nachname} onChange={(e) => set("nachname", e.target.value)} placeholder="Muster" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">PLZ / Ort</label>
                    <input data-testid="leadform-plz" className={inputCls} value={form.plz_ort} onChange={(e) => set("plz_ort", e.target.value)} placeholder="8340 Hinwil" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">E-Mail *</label>
                    <input data-testid="leadform-email" required type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="max@muster.ch" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Telefon *</label>
                    <input data-testid="leadform-telefon" required type="tel" className={inputCls} value={form.telefon} onChange={(e) => set("telefon", e.target.value)} placeholder="+41 79 000 00 00" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Fahrzeuge im Unternehmen</label>
                    <select data-testid="leadform-anzahl" className={inputCls} value={form.anzahl_fahrzeuge} onChange={(e) => set("anzahl_fahrzeuge", e.target.value)}>
                      <option value="">Bitte wählen</option>
                      <option>1</option>
                      <option>2–5</option>
                      <option>6–10</option>
                      <option>11–25</option>
                      <option>über 25</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Gewünschtes KIA Modell</label>
                  <select data-testid="leadform-modell" className={inputCls} value={form.modell} onChange={(e) => set("modell", e.target.value)}>
                    <option value="">Beratung gewünscht</option>
                    {models.map((m) => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Kauf / Leasing</div>
                    <div className="flex gap-2">
                      {["Kauf", "Leasing", "Noch offen"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          data-testid={`leadform-finanzierung-${v.toLowerCase().replace(/\s/g, "-")}`}
                          onClick={() => set("kauf_leasing", v)}
                          className={`flex-1 text-xs font-semibold px-3 py-3 border transition-colors ${form.kauf_leasing === v ? "bg-neutral-950 text-white border-neutral-950" : "border-neutral-300 text-neutral-600 hover:border-neutral-950"}`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Eintauschfahrzeug vorhanden</div>
                    <div className="flex gap-2">
                      {["Ja", "Nein"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          data-testid={`leadform-eintausch-${v.toLowerCase()}`}
                          onClick={() => set("eintausch", v)}
                          className={`flex-1 text-xs font-semibold px-3 py-3 border transition-colors ${form.eintausch === v ? "bg-neutral-950 text-white border-neutral-950" : "border-neutral-300 text-neutral-600 hover:border-neutral-950"}`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">Bemerkungen / Wünsche</label>
                  <textarea data-testid="leadform-bemerkungen" rows={3} className={inputCls} value={form.bemerkungen} onChange={(e) => set("bemerkungen", e.target.value)} placeholder="Optional" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="leadform-datenschutz"
                    type="checkbox"
                    checked={form.datenschutz}
                    onChange={(e) => set("datenschutz", e.target.checked)}
                    className="mt-1 h-4 w-4 accent-neutral-950"
                  />
                  <span className="text-xs text-neutral-600 leading-relaxed">
                    Ich habe die{" "}
                    <a href="https://www.auto-center-hinwil.ch/datenschutz" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-neutral-950" data-testid="leadform-datenschutz-link">
                      Datenschutzerklärung
                    </a>{" "}
                    gelesen und bin einverstanden, dass meine Angaben zur Bearbeitung der Anfrage verwendet werden. *
                  </span>
                </label>
                {error && (
                  <p data-testid="leadform-error" className="text-sm text-red-600">{error}</p>
                )}
                <button
                  data-testid="leadform-submit"
                  type="submit"
                  disabled={sending}
                  className="group w-full inline-flex items-center justify-center gap-3 bg-neutral-950 text-white font-semibold text-sm px-8 py-4 transition-colors hover:bg-neutral-800 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : (
                    <>Firmenofferte anfordern <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
